import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, IconButton, MenuItem, Skeleton, Snackbar, TextField } from '@mui/material';
import StarRounded from '@mui/icons-material/StarRounded';
import ApartmentRounded from '@mui/icons-material/ApartmentRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import AddShoppingCartRounded from '@mui/icons-material/AddShoppingCartRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded';
import HistoryRounded from '@mui/icons-material/HistoryRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import api from '../services/api';
import { money } from '../services/swil';
import { SchemeRibbon, ProductFormIcon, schemePercent } from './OrderParts';
import './SchemeCarousel.css';

const AUTO_ADVANCE_MS = 4500;
const RESUME_AFTER_MS = 8000; // auto-run pauses after a touch, then carries on
const stockOf = (p) => Number(p?.total_stock_qty ?? p?.stock ?? 0) || 0;
const pctOf = (p) => parseFloat(String(p?.scheme ?? '')) || 0;
const companyOf = (p) => String(p?.mkt_grp ?? '').trim();
const SCHEME_STEPS = [80, 70, 30, 20, 10, 0]; // "more than X%" filters from the biggest down, then "All"
const MAX_SHOWN = 60; // cards in the carousel at once; filters narrow the rest

// "Schemes for you": swipeable cards of in-stock scheme products, the customer's usual buys first, each with add to cart.
export default function SchemeCarousel({ customer, cartItems = [], onAddToCart, onUpdateCartItem, onRemoveFromCart }) {
  const navigate = useNavigate();
  const track = useRef(null);
  const [state, setState] = useState({ loading: true, products: [], personal: 0 });
  const [qty, setQty] = useState({});
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const pausedUntil = useRef(0);
  const nudge = () => { pausedUntil.current = Date.now() + RESUME_AFTER_MS; };
  const [edges, setEdges] = useState({ start: true, end: false });
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState({ min: 0, mine: false, company: '' });
  const customerId = customer?.customer_id;

  useEffect(() => {
    if (!customerId) return undefined;
    let alive = true;
    api.get('schemes_for_you.php', { params: { customer_id: customerId } })
      .then(({ data }) => { if (alive) setState({ loading: false, products: data.products || [], personal: data.personal || 0 }); })
      .catch(() => { if (alive) setState({ loading: false, products: [], personal: 0 }); });
    return () => { alive = false; };
  }, [customerId]);

  const products = state.products;
  const companies = useMemo(() => {
    const counts = new Map();
    products.forEach((p) => { const c = companyOf(p); if (c) counts.set(c, (counts.get(c) || 0) + 1); });
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);
  // Counts on each scheme chip respect the other two filters.
  const scoped = useMemo(() => products.filter((p) => (!filter.mine || p.bought_times > 0) && (!filter.company || companyOf(p) === filter.company)), [products, filter.mine, filter.company]);
  const matching = useMemo(() => scoped.filter((p) => pctOf(p) >= filter.min), [scoped, filter.min]);
  const shown = matching.slice(0, MAX_SHOWN);
  const mineCount = useMemo(() => products.filter((p) => p.bought_times > 0 && (!filter.company || companyOf(p) === filter.company) && pctOf(p) >= filter.min).length, [products, filter.company, filter.min]);
  const filtersOn = filter.mine || filter.min > 0 || filter.company;

  const cards = () => [...(track.current?.querySelectorAll('.sfy-card') || [])];
  const onScroll = useCallback(() => {
    const el = track.current; if (!el) return;
    const list = cards(); if (!list.length) return;
    const left = el.scrollLeft;
    let nearest = 0;
    const start = list[0].offsetLeft;
    list.forEach((card, i) => { if (Math.abs(card.offsetLeft - start - left) < Math.abs(list[nearest].offsetLeft - start - left)) nearest = i; });
    setActive(nearest);
    setEdges({ start: left < 8, end: left + el.clientWidth >= el.scrollWidth - 8 });
  }, []);
  const goTo = useCallback((index) => {
    const el = track.current; const list = cards(); if (!el || !list.length) return;
    const target = list[Math.max(0, Math.min(index, list.length - 1))];
    el.scrollTo({ left: target.offsetLeft - list[0].offsetLeft, behavior: 'smooth' });
  }, []);
  const step = (dir) => {
    nudge();
    const el = track.current; if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.85, 240), behavior: 'smooth' });
  };

  // Gentle auto-advance that keeps running: pauses on hover and for a few seconds after a touch. Off with reduced motion.
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || paused || shown.length < 2) return undefined;
    const timer = setInterval(() => {
      const el = track.current; if (!el || Date.now() < pausedUntil.current) return;
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 8) el.scrollTo({ left: 0, behavior: 'smooth' });
      else goTo(active + 1);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [active, paused, shown.length, goTo]);

  useEffect(() => { onScroll(); }, [state.products, onScroll]);
  // A new filter starts the carousel from its first card.
  useEffect(() => {
    track.current?.scrollTo({ left: 0 }); setActive(0);
    // On phones the chip row scrolls sideways: keep the chosen chip in view.
    track.current?.closest('.sfy')?.querySelectorAll('.sfy-chip.is-on').forEach((chip) => chip.scrollIntoView?.({ block: 'nearest', inline: 'nearest' }));
  }, [filter]);

  if (!customerId || (!state.loading && !products.length)) return null;
  const choose = (change) => { nudge(); setFilter((f) => ({ ...f, ...change })); };

  const inCart = (p) => cartItems.find((item) => String(item.product_id) === String(p.product_id));
  const qtyFor = (p) => qty[p.product_id] ?? 1;
  const setFor = (p, value) => setQty((q) => ({ ...q, [p.product_id]: Math.max(1, Math.min(Math.floor(Number(value) || 1), stockOf(p) || 1)) }));
  const limit = (p, value) => Math.max(1, Math.min(Math.floor(Number(value) || 1), stockOf(p) || 1));
  const add = (p) => {
    nudge();
    onAddToCart({ ...p, quantity: qtyFor(p) });
    setToast({ name: p.product_name, text: `${qtyFor(p)} added to cart` });
  };
  // Once in the cart, the stepper edits the cart quantity directly.
  const changeCart = (p, value) => {
    nudge();
    const cart = inCart(p); if (!cart) return;
    const quantity = limit(p, value);
    if (quantity !== cart.quantity) onUpdateCartItem({ ...cart, quantity });
  };
  const remove = (p) => {
    nudge();
    onRemoveFromCart(p.product_id);
    setQty((q) => ({ ...q, [p.product_id]: 1 }));
    setToast({ name: p.product_name, text: 'removed from cart' });
  };

  return <section className="sfy" aria-labelledby="sfy-title"
    onPointerEnter={(e) => { if (e.pointerType === 'mouse') setPaused(true); }} onPointerLeave={(e) => { if (e.pointerType === 'mouse') setPaused(false); }}
    onFocusCapture={(e) => { if (e.target.matches?.(':focus-visible')) setPaused(true); }} onBlurCapture={() => setPaused(false)} onTouchStart={nudge}>
    <header className="sfy-head">
      <div>
        <span className="sfy-kicker"><LocalOfferRounded fontSize="inherit" /> SCHEMES FOR YOU</span>
        <h2 id="sfy-title">Save more on what you order.</h2>
        <p>{state.personal ? `${state.personal} of these are products you already buy, now with a scheme.` : 'In-stock products with a flat scheme this week.'}</p>
      </div>
      <div className="sfy-nav">
        <IconButton aria-label="Previous offers" onClick={() => step(-1)} disabled={edges.start}><ChevronLeftRounded /></IconButton>
        <IconButton aria-label="Next offers" onClick={() => step(1)} disabled={edges.end}><ChevronRightRounded /></IconButton>
      </div>
    </header>

    {!state.loading && <div className="sfy-filters" role="group" aria-label="Filter offers">
      <div className="sfy-chips">
        <button type="button" className={`sfy-chip sfy-chip--mine ${filter.mine ? 'is-on' : ''}`} aria-pressed={filter.mine} onClick={() => choose({ mine: !filter.mine })} disabled={!filter.mine && !mineCount}>
          <StarRounded fontSize="inherit" /> I usually buy <span>{mineCount}</span>
        </button>
        <span className="sfy-chip-sep" aria-hidden="true" />
        {SCHEME_STEPS.map((step) => {
          const count = step ? scoped.filter((p) => pctOf(p) >= step).length : scoped.length;
          return <button type="button" key={step} className={`sfy-chip ${filter.min === step ? 'is-on' : ''}`} aria-pressed={filter.min === step} onClick={() => choose({ min: step })} disabled={!count && filter.min !== step}>
            {step ? `${step}%+` : 'All'} <span>{count}</span>
          </button>;
        })}
      </div>
      {/* Shown once product data carries company names (mkt_grp); SWIL's product feed sends it empty for now. */}
      {companies.length > 0 && <TextField select size="small" className="sfy-company" label="Company" value={filter.company} onChange={(e) => choose({ company: e.target.value })}
        InputProps={{ startAdornment: <ApartmentRounded fontSize="small" className="sfy-company-icon" /> }}>
        <MenuItem value="">All companies</MenuItem>
        {companies.map(([name, count]) => <MenuItem key={name} value={name}>{name} ({count})</MenuItem>)}
      </TextField>}
    </div>}
    {!state.loading && <p className="sfy-showing">
      {matching.length ? (matching.length > MAX_SHOWN ? `Showing the top ${MAX_SHOWN} of ${matching.length} offers, highest scheme first.` : `${matching.length} offer${matching.length === 1 ? '' : 's'}, highest scheme first.`) : 'No offers match these filters.'}
      {filtersOn && <button type="button" onClick={() => choose({ min: 0, mine: false, company: '' })}>Clear filters</button>}
    </p>}

    <div className={`sfy-viewport ${edges.start ? '' : 'fade-start'} ${edges.end ? '' : 'fade-end'}`}>
      <div className="sfy-track" ref={track} onScroll={onScroll} role="list" onPointerDown={nudge}>
        {state.loading ? Array.from({ length: 4 }, (_, i) => <div className="sfy-card sfy-card--skeleton" key={i}><Skeleton width="55%" height={26} /><Skeleton width="85%" height={22} /><Skeleton variant="rounded" height={70} /><Skeleton variant="rounded" height={40} /></div>)
          : shown.map((p) => {
            const pct = schemePercent(p.scheme);
            const saving = (Number(p.ptr) || 0) * (parseFloat(String(p.scheme)) || 0) / 100;
            const stock = stockOf(p); const cart = inCart(p);
            return <article className={`sfy-card ${cart ? 'is-in-cart' : ''}`} role="listitem" key={p.product_id}>
              {p.bought_times > 0 && <span className="sfy-usual"><HistoryRounded fontSize="inherit" /> You buy this</span>}
              <div className="sfy-card-top">
                <SchemeRibbon scheme={p.scheme} />
              </div>
              <div className="sfy-card-name">
                <ProductFormIcon name={p.product_name} />
                <h3 title={p.product_name}>{p.product_name}</h3>
              </div>
              <div className="sfy-price">
                <div><span>PTR</span><strong>{money(p.ptr)}</strong></div>
                <div><span>MRP</span><strong>{money(p.mrp)}</strong></div>
                <div className="sfy-save"><span>You save</span><strong>{money(saving)}<small>/unit</small></strong></div>
              </div>
              <div className="sfy-meta">
                <span className={`sfy-stock ${stock < 20 ? 'is-low' : ''}`}>{stock < 20 ? `Only ${stock} left` : 'In stock'}{pct ? ` · ${pct} off` : ''}</span>
              </div>
              <div className="sfy-actions">
                <div className="sfy-qty">
                  <button type="button" aria-label={`Fewer ${p.product_name}`} onClick={() => (cart ? changeCart(p, cart.quantity - 1) : setFor(p, qtyFor(p) - 1))}>−</button>
                  <input inputMode="numeric" aria-label={`Quantity of ${p.product_name}`} value={cart ? cart.quantity : qtyFor(p)} onChange={(e) => (cart ? changeCart(p, e.target.value) : setFor(p, e.target.value))} />
                  <button type="button" aria-label={`More ${p.product_name}`} onClick={() => (cart ? changeCart(p, cart.quantity + 1) : setFor(p, qtyFor(p) + 1))}>+</button>
                </div>
                {cart ? <>
                  <Button variant="contained" className="sfy-in-cart" onClick={() => navigate('/cart')} startIcon={<CheckCircleRounded />}>In cart</Button>
                  <IconButton className="sfy-delete" aria-label={`Remove ${p.product_name} from cart`} onClick={() => remove(p)}><DeleteOutlineRounded fontSize="small" /></IconButton>
                </> : <Button variant="contained" onClick={() => add(p)} startIcon={<AddShoppingCartRounded />}>Add to cart</Button>}
              </div>
            </article>;
          })}
      </div>
    </div>

    {!state.loading && shown.length > 12 && <div className="sfy-progress" aria-label={`Offer ${active + 1} of ${shown.length}`}>
      <span className="sfy-progress-bar"><i style={{ width: `${((active + 1) / shown.length) * 100}%` }} /></span>
      <span className="sfy-progress-count">{active + 1} / {shown.length}</span>
    </div>}
    {!state.loading && shown.length > 1 && shown.length <= 12 && <div className="sfy-dots" role="tablist" aria-label="Offer position">
      {shown.map((p, i) => <button type="button" key={p.product_id} role="tab" aria-selected={i === active} aria-label={`Offer ${i + 1} of ${shown.length}`} className={i === active ? 'is-active' : ''} onClick={() => { nudge(); goTo(i); }} />)}
    </div>}

    <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)}
      message={toast ? `${toast.name}: ${toast.text}` : ''}
      action={<Button color="inherit" size="small" onClick={() => navigate('/cart')}>View cart</Button>} />
  </section>;
}
